import React, { useState } from 'react';
import { Minimize2, Maximize2 } from 'lucide-react';
import { ConstraintViolation, EventItem } from '../../core/types.ts';
import { EventCard } from './EventCard.tsx';

interface CalendarViewProps {
  events: EventItem[];
  violationsMap: Map<string, ConstraintViolation[]>;
  onEditEvent: (event: EventItem) => void;
  onDeleteEvent: (id: string) => void;
  onRescheduleEvent: (eventId: string, newDateStr: string) => void;
}

// Generate date objects for Sept 28, 2026 to Oct 12, 2026
const DATES: { dateStr: string; dayName: string; formattedDate: string; week: number }[] = [];
const startD = new Date(2026, 8, 28); // Sept 28, 2026

for (let i = 0; i < 15; i++) {
  const d = new Date(startD);
  d.setDate(startD.getDate() + i);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;

  const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
  const formattedDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const week = i < 7 ? 1 : 2;

  DATES.push({ dateStr, dayName, formattedDate, week });
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  violationsMap,
  onEditEvent,
  onDeleteEvent,
  onRescheduleEvent,
}) => {
  const [selectedWeek, setSelectedWeek] = useState<'all' | 1 | 2>('all');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('esn_calendar_collapsed') === 'true';
  });
  const [draggedEventId, setDraggedEventId] = useState<string | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('esn_calendar_collapsed', String(next));
      return next;
    });
  };

  const handleDragStart = (e: React.DragEvent, eventId: string) => {
    setDraggedEventId(eventId);
    e.dataTransfer.setData('text/plain', eventId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverDate !== dateStr) {
      setDragOverDate(dateStr);
    }
  };

  const handleDragLeave = (_e: React.DragEvent) => {
    setDragOverDate(null);
  };

  const handleDrop = (e: React.DragEvent, targetDateStr: string) => {
    e.preventDefault();
    setDragOverDate(null);
    const eventId = e.dataTransfer.getData('text/plain') || draggedEventId;
    if (eventId) {
      onRescheduleEvent(eventId, targetDateStr);
    }
    setDraggedEventId(null);
  };

  const filteredDates = DATES.filter(d => {
    if (selectedWeek === 'all') return true;
    return d.week === selectedWeek;
  });

  return (
    <div className="calendar-container">
      <div className="calendar-view-header">
        <div className="view-controls">
          <button
            className={`view-btn ${selectedWeek === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedWeek('all')}
          >
            Full O-Weeks (15 Days)
          </button>
          <button
            className={`view-btn ${selectedWeek === 1 ? 'active' : ''}`}
            onClick={() => setSelectedWeek(1)}
          >
            Week 1 (Sept 28 - Oct 04)
          </button>
          <button
            className={`view-btn ${selectedWeek === 2 ? 'active' : ''}`}
            onClick={() => setSelectedWeek(2)}
          >
            Week 2 (Oct 05 - Oct 12)
          </button>
        </div>

        <div className="view-options">
          <button
            className={`view-btn collapse-toggle-btn ${isCollapsed ? 'active' : ''}`}
            onClick={toggleCollapse}
            title={isCollapsed ? 'Switch to Expanded View' : 'Switch to Collapsed View'}
          >
            {isCollapsed ? <Maximize2 size={16} strokeWidth={1.8} /> : <Minimize2 size={16} strokeWidth={1.8} />}
            <span>{isCollapsed ? 'Expand Events' : 'Collapse Events'}</span>
          </button>
        </div>
      </div>

      <div className="calendar-grid">
        {filteredDates.map(dayObj => {
          // Get events matching this date
          const dayEvents = events.filter(ev => ev.start.startsWith(dayObj.dateStr));

          // Sort chronologically
          dayEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

          const isDragOver = dragOverDate === dayObj.dateStr;

          return (
            <div key={dayObj.dateStr} className={`day-column ${isDragOver ? 'drag-over' : ''}`}>
              <div className="day-header">
                <div>
                  <span className="day-title">{dayObj.dayName}</span>
                  <span className="day-date">, {dayObj.formattedDate}</span>
                </div>
                <span className="day-event-count">{dayEvents.length}</span>
              </div>

              <div
                className="day-drop-zone"
                onDragOver={e => handleDragOver(e, dayObj.dateStr)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, dayObj.dateStr)}
              >
                {dayEvents.map(ev => (
                  <EventCard
                    key={ev.id}
                    event={ev}
                    violations={violationsMap.get(ev.id) || []}
                    onEdit={onEditEvent}
                    onDelete={onDeleteEvent}
                    onDragStart={handleDragStart}
                    isCollapsed={isCollapsed}
                  />
                ))}

                {dayEvents.length === 0 && (
                  <div className="day-empty-state">
                    <span>Drag events here or click New Event</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
